import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ui from "@/constants/ui";
import useTranslation from "@/hooks/useTranslation";
import useUser from "@/stores/user";
import { formatDateTime } from "@/utils/funcs";
import { useRequest } from "ahooks";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import Icon from "@/components/Icon";
import notification from "@/icons/notification.svg";
import unreadNotification from "@/icons/unread-notification.svg";

function NotificationCenter() {
  const t = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [containsUnread, setContainsUnread] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [businessType, setBusinessType] = useState("");
  setBusinessType;
  const { isAlreadySigned, currentActiveAccount } = useUser();
  const alreadySigned = isAlreadySigned();

  const getAllNotifications = async () => {
    try {
      if (!currentActiveAccount?.keys?.apiKey) {
        return { data: { data: { dataList: [] } } };
      }
      return await axios.get("/v1/private/user/getSiteMessagePage", {
        params: {
          filterBusinessType: businessType,
        },
        interceptError: false,
      });
    } catch {
      return false;
    }
  };

  const { data: notificationRes, run: runGetAllNotifications } = useRequest(getAllNotifications, {
    manual: true, // Do not run automatically
    pollingInterval: 60000, // Polling every 60000 milliseconds (1 minute)
    pollingWhenHidden: false, // Optional: Set to true if you want to continue polling even when the browser tab is inactive
    refreshDeps: [currentActiveAccount?.keys?.apiKey],
  });

  // Start polling when alreadySigned is true
  useEffect(() => {
    if (alreadySigned) {
      runGetAllNotifications();
    }
  }, [alreadySigned, runGetAllNotifications]);

  useEffect(() => {
    if (notificationRes && alreadySigned) {
      const allNotifications = notificationRes.data.data.dataList;
      setNotifications(allNotifications);

      // Check if unread notifications found
      const unread = allNotifications.some((item) => !item.isRead);
      setContainsUnread(unread);
    }
  }, [notificationRes]);

  const triggerRead = async (messageId) => {
    try {
      await axios.post("/v1/private/user/readSiteMessage", {
        params: {
          messageId: messageId,
        },
      });
      setNotifications((notifications) => notifications.map((n) => ({ ...n, isRead: n.id === messageId ? true : n.isRead })));
    } catch {
      return false;
    }
  };

  // Format notifications content
  function formatContent(content) {
    // Capitilize 1st character after `\n` or `\n `
    const withUpperCase = content.replace(/\\n\s*(\S)/g, (LAST_PRICE, firstCharacterAfterNewline) => {
      return "\\n" + firstCharacterAfterNewline.toUpperCase();
    });

    // Replace \n to <br />
    const withLineBreaks = withUpperCase.replace(/\\n/g, "<br />");
    return withLineBreaks;
  }

  return (
    <Sheet open={isOpen} onOpenChange={(s) => setIsOpen(s)} className="bg-primary">
      <SheetTrigger asChild>
        <div className={ui?.headerIconLinkClassName}>
          {/* Header icon */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21 7.16601C20.8235 7.25744 20.6178 7.27502 20.4283 7.2149C20.2388 7.15478 20.0809 7.02187 19.9894 6.84539C19.2735 5.43116 18.1899 4.23586 16.8525 3.38508C16.7692 3.33251 16.697 3.26403 16.6401 3.18357C16.5832 3.10311 16.5427 3.01223 16.521 2.91612C16.4992 2.82001 16.4966 2.72055 16.5133 2.62343C16.5299 2.52631 16.5656 2.43342 16.6181 2.35008C16.6707 2.26673 16.7392 2.19455 16.8196 2.13766C16.9001 2.08078 16.991 2.0403 17.0871 2.01853C17.1832 1.99677 17.2827 1.99415 17.3798 2.01082C17.4769 2.02749 17.5698 2.06313 17.6531 2.1157C19.2118 3.1137 20.4775 4.50779 21.3206 6.15539C21.4121 6.33194 21.4296 6.53756 21.3695 6.72707C21.3094 6.91658 21.1765 7.07446 21 7.16601ZM3.34783 7.25039C3.48523 7.25033 3.61998 7.21253 3.73736 7.14111C3.85474 7.06969 3.95025 6.96739 4.01346 6.84539C4.72933 5.43116 5.81291 4.23586 7.15033 3.38508C7.31866 3.27891 7.43792 3.11022 7.48187 2.91612C7.52583 2.72202 7.49087 2.51841 7.38471 2.35008C7.27854 2.18175 7.10985 2.06249 6.91575 2.01853C6.72165 1.97458 6.51804 2.00953 6.34971 2.1157C4.79102 3.1137 3.52537 4.50779 2.68221 6.15539C2.62299 6.26969 2.59419 6.3973 2.59858 6.52596C2.60297 6.65461 2.6404 6.77997 2.70726 6.88997C2.77413 6.99996 2.86818 7.0909 2.98037 7.15402C3.09256 7.21715 3.2191 7.25033 3.34783 7.25039ZM20.7947 16.9948C20.9276 17.2226 20.998 17.4815 20.9989 17.7452C20.9998 18.009 20.9312 18.2683 20.7999 18.4971C20.6686 18.7259 20.4793 18.9159 20.251 19.0482C20.0228 19.1805 19.7638 19.2502 19.5 19.2504H15.675C15.5029 20.0981 15.043 20.8602 14.3732 21.4077C13.7035 21.9551 12.865 22.2541 12 22.2541C11.135 22.2541 10.2966 21.9551 9.62682 21.4077C8.95707 20.8602 8.49715 20.0981 8.32502 19.2504H4.50002C4.23641 19.2499 3.9776 19.1799 3.74965 19.0475C3.5217 18.9151 3.33266 18.725 3.20158 18.4963C3.0705 18.2676 3.002 18.0084 3.00299 17.7448C3.00398 17.4812 3.07441 17.2225 3.20721 16.9948C4.05189 15.537 4.50002 13.4641 4.50002 11.0004C4.50002 9.01126 5.29019 7.10361 6.69672 5.69709C8.10324 4.29056 10.0109 3.50039 12 3.50039C13.9891 3.50039 15.8968 4.29056 17.3033 5.69709C18.7098 7.10361 19.5 9.01126 19.5 11.0004C19.5 13.4632 19.9481 15.536 20.7947 16.9948ZM14.1206 19.2504H9.87939C10.0348 19.6886 10.3221 20.0679 10.7018 20.3361C11.0816 20.6044 11.5351 20.7484 12 20.7484C12.465 20.7484 12.9185 20.6044 13.2982 20.3361C13.6779 20.0679 13.9653 19.6886 14.1206 19.2504ZM19.5 17.7504C18.5025 16.0376 18 13.767 18 11.0004C18 9.40909 17.3679 7.88297 16.2427 6.75775C15.1174 5.63253 13.5913 5.00039 12 5.00039C10.4087 5.00039 8.8826 5.63253 7.75738 6.75775C6.63216 7.88297 6.00002 9.40909 6.00002 11.0004C6.00002 13.7679 5.49564 16.0385 4.50002 17.7504H19.5Z"
              fill="currentColor"
            />
          </svg>

          {/* Header Red dot */}
          {containsUnread && (
            <div className="absolute">
              <div className="relative bottom-[8px] left-[12px] rounded-full bg-error w-[8px] h-[8px]" />
            </div>
          )}
        </div>
      </SheetTrigger>

      <SheetContent
        closeClassName="top-6 right-4 size-5"
        className={twMerge(ui?.SheetContentClassName, "!bg-[--dialog] max-w-[360px] w-[360px]")}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <SheetHeader className="mt-4 mr-7 px-4 flex flex-row justify-between items-center">
          <SheetTitle className="">{t("notifications")}</SheetTitle>
          {notifications?.length > 0 && (
            <div
              className="!mt-0 !mb-0 text-[--font-gray] hover:text-primary-content cursor-pointer"
              onClick={() => {
                notifications.forEach((n) => {
                  if (!n.isRead) {
                    triggerRead(n.id);
                  }
                });
              }}
            >
              {t("allRead")}
            </div>
          )}
        </SheetHeader>

        {/* Notifications list */}
        {notifications && notifications.length > 0 ? (
          <div className="text flex flex-col h-[calc(100%_-_133px)] overflow-y-auto no-scrollbar">
            {notifications.map((item, index) => (
              <div
                key={`notification-${index}`}
                className={`flex flex-col gap-2 px-4 py-2 hover:bg-bg-secondary hover:cursor-pointer`}
                onClick={() => {
                  if (!item.isRead) {
                    triggerRead(item.id);
                  }
                }}
              >
                <div className="flex flex-row justify-between">
                  <div className={twMerge("flex flex-row items-center gap-1.5 text-xs leading-none font-medium", item.isRead ? "text-[--font-gray]" : "text-primary")}>
                    <Icon icon={item.isRead ? notification : unreadNotification} className="w-3.5" />
                    <span className="text-xs leading-none font-medium">{item.isRead ? t("hadReadNotification") : t("newNotification")}</span>
                  </div>
                  <div className="text-xs text-[--font-gray] leading-none font-medium">{formatDateTime(item.createdTime, "YYYY-mm-dd HH:MM:SS")}</div>
                </div>
                <div className={`text-xs leading-none font-medium`}>{item.title}</div>
                <div className="flex flex-col gap-[6px] text-[--font-gray] text-xs leading-4">
                  <div dangerouslySetInnerHTML={{ __html: formatContent(item.content) }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-[24px] flex justify-center items-center text-center text-[14px] leading-[140%] text-foreground-secondary">{t("notifications.noNotifications")}</div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default NotificationCenter;
